import { useCallback, memo } from "react";
import PropTypes from "prop-types";
import ReactTable from "react-table-6";
import { Button, Typography, MenuItem, FormControl, Select, InputAdornment, Input, Checkbox } from "@mui/material";
import { NavigateBefore, NavigateNext, Search } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { shallow } from "zustand/shallow";
import { isFuzzyMatch } from "../utils/index.js";
import useGlobalState from "../use-global-state.js";
import DateRangePicker from "./DateRangePicker.js";

const useStyles = makeStyles((theme) => ({
	paginationButton: {
		backgroundColor: theme.palette.secondary.main,
		borderRadius: 3 * theme.shape.borderRadius,
		color: "white!important",
		"&:hover": {
			backgroundColor: theme.palette.secondaryDark.main,
		},
	},
	formControl: {
		marginRight: theme.spacing(0.5),
		minWidth: 70,
	},
	input: {
		padding: `${theme.spacing(0.5)} !important`,
		color: "black",
		border: `${theme.spacing(0.1)} solid black`
	},
	select: {
		color: "black",
		fill: "white",
		backgroundColor: "white",
		border: `${theme.spacing(0.1)} solid ${theme.palette.primary.main}`,
		borderColor: "grey.main",
		borderRadius: 1.5 * theme.shape.borderRadius,
		height: "35px",
		marginTop: "-0.05rem"
	}
}));

const defaultFilterMethod = ({ id, value }, row) => isFuzzyMatch(row[id], value);
const getTdProps = () => ({ style: { alignSelf: "center", textAlign: "center" } });
const getTrProps = () => ({ style: { borderBottom: "1px solid" } });

const Table = (props) => {
	const {
		data,
		columns,
		noDataText,
		defaultSorted,
		className,
		customPageSize,
		showPageSizeOptions = true,
		dropdownOptions = [{ value: "USER", label: "USER" }, { value: "ADMIN", label: "ADMIN" }],
		...otherProps
	} = props;
	const { defaultPageSize } = useGlobalState((e) => ({
		defaultPageSize: e.defaultPageSize,
	}), shallow);
	const classes = useStyles();
	const theme = useTheme();

	const FilterComponent = useCallback(({ filter = { value: "" }, onChange }) => (
		<Input
			disableUnderline
			type="search"
			value={filter.value}
			name="search"
			sx={{ width: "100%", position: "relative", ">.MuiInput-input": { height: "100%" }, }}
			startAdornment={<InputAdornment sx={{ position: "absolute" }} position="end"><Search sx={{ display: filter?.value ? "none" : "block" }} /></InputAdornment>}
			onChange={(event) => onChange(event.target.value)}
		/>
	), []);


	const DropdownFilterComponent = useCallback(
		({ filter = { value: [] }, onChange, options }) => (
			<FormControl variant="standard" sx={{ width: "100%" }}>
				<Select
					multiple
					value={filter.value}
					onChange={(event) => onChange(event.target.value)}
					renderValue={(selected) => selected.join(", ")}
					label="Filter"
					className={classes.select}
				>
					{options.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							<Checkbox checked={filter?.value?.includes(option?.value)} />
							{option.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		),
		[]
	);

	const DateRangePickerComponent = useCallback(
		({ onChange }) => (
			<DateRangePicker onChange={onChange} />
		),
		[]
	);

	const PreviousComponent = useCallback((prps) => (
		<Button variant="outlined" size="small" {...prps} className={classes.paginationButton}>
			<NavigateBefore />
		</Button>
	), [classes.paginationButton]);

	const NextComponent = useCallback((prps) => (
		<Button variant="outlined" size="small" {...prps} className={classes.paginationButton}>
			<NavigateNext />
		</Button>
	), [classes.paginationButton]);

	const getTheadTrProps = useCallback(() => ({
		style: {
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.common.white,
			padding: 2,
		},
	}), [theme.palette.common.white, theme.palette.common.white]);

	const getTableProps = useCallback(() => ({
		style: {
			border: `${theme.spacing(0.5)} solid ${theme.palette.primary.main}`,
			borderRadius: 2.5 * theme.shape.borderRadius,
		},
	}), [theme]);

	const getPaginationProps = useCallback(() => ({
		style: { margin: theme.spacing(2, 5), backgroundColor: "transparent", border: "none", boxShadow: "none", color: "black" },
	}), [theme]);

	const getNoDataProps = useCallback(() => {
		<Typography sx={{ color: "grey.main" }}>Δεν υπάρχουν προσκεκλημένοι χρήστες</Typography>
	})

	const getColumnFilterComponent = (column) => {
		const { filterOptions = dropdownOptions } = column;

		if (column.useDropdownFilter) {
			return (props) => <DropdownFilterComponent {...props} options={filterOptions} />;
		}
		if (column.useDatePickerFilter) {
			return DateRangePickerComponent;
		}
		return FilterComponent;
	};

	const columnsWithFilter = columns.map((col) => ({
		...col,
		Filter: getColumnFilterComponent(col),
	}));

	return (
		<ReactTable
			showPageSizeOptions={showPageSizeOptions}
			showPaginationBottom={showPageSizeOptions}
			data={data}
			noDataText={noDataText}
			columns={columnsWithFilter}
			defaultSorted={defaultSorted}
			defaultFilterMethod={defaultFilterMethod}
			defaultPageSize={customPageSize || defaultPageSize}
			minRows={1}
			className={clsx(className)}
			getTdProps={getTdProps}
			getTrProps={getTrProps}
			getTheadTrProps={getTheadTrProps}
			getTheadFilterTrProps={getTheadTrProps}
			getTableProps={getTableProps}
			getPaginationProps={getPaginationProps}
			getNoDataProps={getNoDataProps}
			PreviousComponent={PreviousComponent}
			showPageJump={false}
			NextComponent={NextComponent}
			{...otherProps}
		/>
	);
};

Table.propTypes = {
	showPageSizeOptions: PropTypes.bool,
	data: PropTypes.array,
	columns: PropTypes.array.isRequired,
	noDataText: PropTypes.string,
	defaultSorted: PropTypes.array,
	className: PropTypes.string,
	customPageSize: PropTypes.number,
};
Table.defaultProps = { data: [], noDataText: "No data available!", defaultSorted: [{ id: "updatedAt", desc: true }] };
Table.whyDidYouRender = false;

export default memo(Table);
